'use client'

import Image from 'next/image'
import { useState } from 'react'
import Link from 'next/link'

interface HomeProceduresProps {
	heading?: {
		title?: string
	}
	surgical_content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title?: string
					embed_url?: string
					menu_items?: {
						menu_items_id: {
							label?: string
							href?: string
						}
					}[]
					url?: string
				}
			}[]
		}
	}[]
	nonsurgical_content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title?: string
					embed_url?: string
					menu_items?: {
						menu_items_id: {
							label?: string
							href?: string
						}
					}[]
					url?: string
				}
			}[]
		}
	}[]
}

export const fields = [
	'heading.title',

	'surgical_content_collection.id',
	'surgical_content_collection.site_content_collections_id.collection.item:site_content.title',

	'surgical_content_collection.site_content_collections_id.collection.item:videos.embed_url',

	'surgical_content_collection.site_content_collections_id.collection.item:menus.menu_items.menu_items_id',
	'surgical_content_collection.site_content_collections_id.collection.item:menus.menu_items.menu_items_id.label',
	'surgical_content_collection.site_content_collections_id.collection.item:menus.menu_items.menu_items_id.href',

	'surgical_content_collection.site_content_collections_id.collection.item:external_links.title',
	'surgical_content_collection.site_content_collections_id.collection.item:external_links.url',

	'nonsurgical_content_collection.id',
	'nonsurgical_content_collection.site_content_collections_id.collection.item:site_content.title',

	'nonsurgical_content_collection.site_content_collections_id.collection.item:videos.embed_url',

	'nonsurgical_content_collection.site_content_collections_id.collection.item:menus.menu_items.menu_items_id',
	'nonsurgical_content_collection.site_content_collections_id.collection.item:menus.menu_items.menu_items_id.label',
	'nonsurgical_content_collection.site_content_collections_id.collection.item:menus.menu_items.menu_items_id.href',

	'nonsurgical_content_collection.site_content_collections_id.collection.item:external_links.title',
	'nonsurgical_content_collection.site_content_collections_id.collection.item:external_links.url',
] as const

export default function HomeProcedures({ data }: { data: HomeProceduresProps }) {
	const [surgicalHover, setSurgicalHover] = useState(false)
	const [nonSurgicalHover, setNonSurgicalHover] = useState(false)

	const surgicalCollection = data?.surgical_content_collection?.[0]?.site_content_collections_id.collection
	const nonSurgicalCollection = data?.nonsurgical_content_collection?.[0]?.site_content_collections_id.collection

	const surgicalTitle = surgicalCollection?.find(c => c.item.title)?.item.title
	const surgicalMenuItems = surgicalCollection?.find(c => c.item.menu_items)?.item.menu_items || []
	const surgicalVideoUrl = surgicalCollection?.find(c => c.item.embed_url)?.item.embed_url
	const surgicalViewAllLink = surgicalCollection?.find(c => c.item.url)?.item

	const nonSurgicalTitle = nonSurgicalCollection?.find(c => c.item.title)?.item.title
	const nonSurgicalMenuItems = nonSurgicalCollection?.find(c => c.item.menu_items)?.item.menu_items || []
	const nonSurgicalVideoUrl = nonSurgicalCollection?.find(c => c.item.embed_url)?.item.embed_url
	const nonSurgicalViewAllLink = nonSurgicalCollection?.find(c => c.item.url)?.item

	return (
		<section
			id='homeprocedures'
			className='HomeProcedures w-full bg-[#e7ddd4] max-md:bg-[#f6f6f6]'
		>
			<div className='container py-12'>
				<h2 className='text-4xl md:text-5xl font-agopus'>{data?.heading?.title || 'Our Procedures'}</h2>
			</div>
			<div className='w-full'>
				<div className='flex flex-col md:flex-row gap-6 mx-auto'>
					{/* Surgical Procedures */}
					<div
						className='relative flex-1 min-h-[42rem] md:min-h-[42rem] max-md:h-[647px] overflow-hidden cursor-pointer group w-full max-md:rounded-tl-[50px]'
						onMouseEnter={() => setSurgicalHover(true)}
						onMouseLeave={() => setSurgicalHover(false)}
					>
						<video
							src={surgicalVideoUrl || ''}
							autoPlay
							muted
							loop
							playsInline
							className='absolute inset-0 w-full h-full object-cover'
						/>

						<div className='absolute inset-0'>
							<div className='flex justify-center relative z-[1]'>
								<span className='inline-block px-10 py-5 bg-brown-light md:bg-brown-light max-md:bg-transparent rounded-b-lg text-3xl md:text-4xl font-brandon tracking-[2.52px] uppercase text-black max-md:text-white max-md:w-full max-md:pt-[3.125rem] max-md:px-6'>
									{surgicalTitle}
								</span>
							</div>

							<div
								className={`absolute inset-0 max-md:bg-black/50 md:bg-brown-light/50 transition-all duration-[1000ms] ease-in-out flex flex-col items-center justify-center max-md:px-6 pt-20 ${
									surgicalHover
										? 'opacity-100 translate-y-0'
										: 'max-md:opacity-100 max-md:translate-y-0 opacity-0 -translate-y-full'
								}`}
							>
								<div className='space-y-4 mb-6 max-md:w-full'>
									{surgicalMenuItems.map((item, index) => (
										<Link
											href={item.menu_items_id.href || '#'}
											key={index}
											className='block max-md:text-left text-center text-white max-md:text-2xl text-3xl font-brandon hover:text-black transition-colors'
										>
											{item.menu_items_id.label}
										</Link>
									))}
								</div>

								{surgicalViewAllLink && (
									<Link
										href={surgicalViewAllLink.url || '#'}
										className='mt-8 inline-block px-12 py-5 bg-brown rounded-lg text-xl text-white font-brandon hover:bg-brown/90 transition-colors max-md:w-full text-center'
									>
										{surgicalViewAllLink.title || 'View All Procedures'}
									</Link>
								)}
							</div>
						</div>
					</div>

					{/* Non-Surgical Procedures */}
					<div
						className='relative flex-1 min-h-[42rem] md:min-h-[42rem] max-md:h-[647px] overflow-hidden cursor-pointer group w-full max-md:rounded-br-[50px]'
						onMouseEnter={() => setNonSurgicalHover(true)}
						onMouseLeave={() => setNonSurgicalHover(false)}
					>
						<video
							src={nonSurgicalVideoUrl || ''}
							autoPlay
							muted
							loop
							playsInline
							className='absolute inset-0 w-full h-full object-cover'
						/>
						<div className='absolute inset-0'>
							<div className='flex justify-center relative z-[1]'>
								<span className='inline-block px-10 py-5 bg-[#d1c1b4] md:bg-[#d1c1b4] max-md:bg-transparent rounded-b-lg text-3xl md:text-4xl font-brandon tracking-[2.52px] uppercase text-black max-md:text-white max-md:w-full max-md:pt-[3.125rem] max-md:px-6'>
									{nonSurgicalTitle}
								</span>
							</div>

							<div
								className={`absolute inset-0 max-md:bg-black/50 md:bg-brown-light/50 transition-all duration-[1000ms] ease-in-out flex flex-col items-center justify-center max-md:px-6 pt-20 ${
									nonSurgicalHover
										? 'opacity-100 translate-y-0'
										: 'max-md:opacity-100 max-md:translate-y-0 opacity-0 -translate-y-full'
								}`}
							>
								<div className='space-y-4 mb-6 max-md:w-full'>
									{nonSurgicalMenuItems.map((item, index) => (
										<Link
											href={item.menu_items_id.href || '#'}
											key={index}
											className='block max-md:text-left text-center text-white max-md:text-2xl text-3xl font-brandon hover:text-black transition-colors'
										>
											{item.menu_items_id.label}
										</Link>
									))}
								</div>

								{nonSurgicalViewAllLink && (
									<Link
										href={nonSurgicalViewAllLink.url || '#'}
										className='mt-8 inline-block px-12 py-5 bg-brown rounded-lg text-xl text-white font-brandon hover:bg-brown/90 transition-colors max-md:w-full text-center'
									>
										{nonSurgicalViewAllLink.title || 'View All Procedures'}
									</Link>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}